import { Component, EventEmitter, OnInit, OnDestroy, Output, ChangeDetectionStrategy, ChangeDetectorRef, ElementRef, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  normalizeFilterParams,
} from '../../../shared/workspace-element-context';
import { Form1118SchdJService } from './form-1118-schd-j.service';
import { ConfirmationModalConfig } from '../../../shared/components/confirmation-modal/confirmation-modal.component';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import * as _ from 'lodash-es';

@Component({
    selector: 'gtw-form-1118-schd-j',
    templateUrl: './form-1118-schd-j.component.html',
    styleUrls: ['./form-1118-schd-j.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class Form1118SchdJComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);


  private readonly subs = new Subscription();

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  // ---- Global / Input state ----
  globalService: any;
  GENERAL_CONFIG: any;
  USER_SETTINGS: any;
  filterParams: any = {};

  private isInitialized = false;

  // ---- User info ----
  logged_in_user: string = '';

  // ---- Filter dropdowns ----
  filteredTaxYearData: any[] = [];
  selectedTaxYear: any;
  scenarioDropDown: any[] = [];
  selectedScenario: any;
  filingGroupDropdown: any[] = [];
  selectedFG: any;
  filerEntityDropdown: any[] = [];
  selectedFE: any;

  // ---- Loading flags ----
  filterLoading = false;
  scenarioLoading = false;
  filingGrpLoading = false;
  filerEntityLoading = false;
  showSpinner = false;
  showBanner = true;

  // ---- Form data ----
  groupedFormLines: any[] | null = null;
  groupedFormLinesPartII: any[] | null = null;
  groupedFormLinesPartIII: any[] | null = null;
  groupedFormLinesPartIV: any[] | null = null;

  formColumnsPartI: any[] | null = null;
  formColumnsPartII: any[] | null = null;
  formColumnsPartIII: any[] | null = null;
  formColumnsPartIV: any[] | null = null;

  // ---- Keys ----
  jcd_key: string = '';
  jcd_ta_key: string = '';
  sourceKey: string = '';

  // ---- Admin / Lock ----
  isAdmin = false;
  is_locked: any = null;

  // ---- Save ----
  isSaveClicked = false;

  // ---- Sub-total / Recapture line config ----
  partOnesubTotalLines = ['3', '6', '8', '11'];
  subTotalLinesPartII: string[] | null = null;
  subTotalLinesPartIII = ['5'];
  subTotalLinesPartIV = ['4', '6'];

  recaptureLinesPartIII = ['4'];
  recaptureLinesPartIV = ['5'];

  // ---- Activity ----
  activity_name: string = '';
  scenario: string = '';
  curentyear: string = '';

  // ---- Confirmation Modal ----
  showConfirmDialog: boolean = false;
  modalConfig: ConfirmationModalConfig = {
    title: '',
    message: ''
  };

  constructor(
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private form1118SchdJService: Form1118SchdJService,
    private gtwSnackbarService: GtwSnackbarService,
    private cdr: ChangeDetectorRef,
    private el: ElementRef
  ) { }

  ngOnInit(): void {
    this.gtwWorkspace.hideDataFilters.next(true);
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.tryInit();
    }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.tryInit();
    }));
    this.tryInit();
  }

  private applyWorkspaceContext(): void {
    this.isInitialized = false;
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.USER_SETTINGS = header.userSettingsData || {};
    this.globalService = { ...header, inputs: this.gtwGlobal.inputs };
    this.filterParams = normalizeFilterParams(activeScreen);
    this.cdr.detectChanges();
  }

  private tryInit(): void {
    if (this.isInitialized) return;
    if (this.globalService && this.GENERAL_CONFIG) {
      this.isInitialized = true;
      this.init();
    }
  }

  // ===================== INITIALISATION =====================

  init(): void {
    this.scenario = this.filterParams?.scenario || this.globalService?.globalParams?.scenario || '';
    this.jcd_key = this.filterParams?.jcd_key || this.globalService?.globalParams?.jcd_key || '';
    this.jcd_ta_key = this.filterParams?.jcd_ta_key || this.globalService?.globalParams?.jcd_ta_key || '';
    this.activity_name = this.filterParams?.activity_name || this.globalService?.activity_name || this.globalService?.globalParams?.activity_name || '';
    this.curentyear = this.filterParams?.tax_year || this.globalService?.globalParams?.tax_year || '';

    // Check lock status
    this.checkLockStatus();

    // Load tax year dropdown
    this.getTaxYearDropDown();
  }

  checkLockStatus(): void {
    this.form1118SchdJService.checkLockStatus(
      this.GENERAL_CONFIG.base_url,
      this.scenario,
      this.jcd_key,
      this.globalService,
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        this.is_locked = data?.jsonObject;
        if (this.is_locked && this.is_locked[0]?.LOCK_STATUS === 'Y') {
          this.isAdmin = false;
        }
        this.cdr.detectChanges();
      },
      (error: any) => {
        console.error('Error checking lock status:', error);
      }
    );
  }

  // ===================== FILTER DROPDOWNS =====================

  getTaxYearDropDown(): void {
    // Build from global inputs if available
    const taxYearInputs = this.globalService?.inputs?.tax_year;
    if (taxYearInputs) {
      this.filteredTaxYearData = taxYearInputs.values || [];
      this.selectedTaxYear = taxYearInputs.selected || this.filteredTaxYearData[0];
    } else {
      // Fallback: build from curentyear
      this.filteredTaxYearData = [{ value: this.curentyear, label: this.curentyear }];
      this.selectedTaxYear = this.filteredTaxYearData[0];
    }
    this.getScenarioDropDown();
  }

  getScenarioDropDown(): void {
    if (!this.selectedTaxYear) return;
    this.scenarioLoading = true;
    this.cdr.detectChanges();

    this.form1118SchdJService.getScenarioDropDown(
      this.GENERAL_CONFIG.base_url,
      this.selectedTaxYear.value,
      this.globalService,
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        this.scenarioDropDown = data?.jsonObject || [];
        this.selectedScenario = this.scenarioDropDown[0];
        this.scenarioLoading = false;
        this.filterLoading = false;
        this.getFilingGroupDropdown();
        this.cdr.detectChanges();
      },
      (error: any) => {
        console.error('Error loading scenario dropdown:', error);
        this.scenarioLoading = false;
        this.cdr.detectChanges();
      }
    );
  }

  getFilingGroupDropdown(): void {
    if (!this.selectedTaxYear || !this.selectedScenario) return;
    this.filingGrpLoading = true;
    this.cdr.detectChanges();

    this.form1118SchdJService.getFilingGroupDropdown(
      this.GENERAL_CONFIG.base_url,
      this.selectedTaxYear.value,
      this.selectedScenario.VALUE,
      this.globalService,
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        this.filingGroupDropdown = data?.jsonObject || [];
        this.selectedFG = this.filingGroupDropdown[0];
        this.filingGrpLoading = false;
        this.getFilerEntityDropdown();
        this.cdr.detectChanges();
      },
      (error: any) => {
        console.error('Error loading filing group dropdown:', error);
        this.filingGrpLoading = false;
        this.cdr.detectChanges();
      }
    );
  }

  getFilerEntityDropdown(): void {
    if (!this.selectedFG || this.selectedFG.VALUE === '') return;
    this.filerEntityLoading = true;
    this.cdr.detectChanges();

    this.form1118SchdJService.getFilerEntityDropdown(
      this.GENERAL_CONFIG.base_url,
      this.selectedTaxYear.value,
      this.selectedScenario.VALUE,
      this.selectedFG.VALUE,
      this.globalService,
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        const filerEntityArr = data?.jsonObject || [];
        this.filerEntityDropdown = _.filter(filerEntityArr, { CONSOL_GROUP_KEY: this.selectedFG.VALUE });
        this.selectedFE = this.filerEntityDropdown[0];
        this.filerEntityLoading = false;
        this.cdr.detectChanges();
      },
      (error: any) => {
        console.error('Error loading filer entity dropdown:', error);
        this.filerEntityLoading = false;
        this.cdr.detectChanges();
      }
    );
  }

  // ===================== REFRESH / FILTER =====================

  refresh(): void {
    if (!this.selectedFE || this.selectedFE.FILER_LE_KEY === '') {
      this.gtwSnackbarService.openSnackBar('Please select all the filters.', 'gtw-snackbar--red');
      return;
    }
    this.showBanner = false;
    this.showSpinner = true;
    this.cdr.detectChanges();
    this.getFormColumnDescriptions();
    this.getFormLinesAndDescriptions();
  }

  // ===================== FORM DATA LOADING =====================

  getFormColumnDescriptions(): void {
    this.formColumnsPartI = null;
    this.formColumnsPartII = null;
    this.formColumnsPartIII = null;
    this.formColumnsPartIV = null;

    this.form1118SchdJService.getFormColumnDescriptions(
      this.GENERAL_CONFIG.base_url,
      this.selectedTaxYear.value,
      this.globalService,
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        const formAllColumns = data?.jsonObject || [];
        const formColumnsByPart = _.groupBy(formAllColumns, 'CODE_GROUP');
        this.formColumnsPartI = formColumnsByPart['F1118_JPI'] || null;
        this.formColumnsPartII = formColumnsByPart['F1118_JPII'] || null;
        this.formColumnsPartIII = formColumnsByPart['F1118_JPIII'] || null;
        this.formColumnsPartIV = formColumnsByPart['F1118_JPIV'] || null;
        this.cdr.detectChanges();
      },
      (error: any) => {
        console.error('Error loading column descriptions:', error);
      }
    );
  }

  getFormLinesAndDescriptions(): void {
    this.groupedFormLines = null;
    this.groupedFormLinesPartII = null;
    this.groupedFormLinesPartIII = null;
    this.groupedFormLinesPartIV = null;

    // Always General in case of Schedule J
    const basketToFetch = 'GENLIM';

    this.form1118SchdJService.getFormLinesAndDescriptions(
      this.GENERAL_CONFIG.base_url,
      basketToFetch,
      this.selectedTaxYear.value,
      this.selectedScenario.VALUE,
      this.selectedFE.FILER_LE_KEY,
      this.globalService,
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        const formLinesAllPart = data?.jsonObject || [];
        const formLinesByPart = _.groupBy(formLinesAllPart, 'CODE_GROUP');

        const formLines = formLinesByPart['F1118_JPI'];
        this.groupLineCols(formLines, 'I', this.partOnesubTotalLines, null);

        const formLinesPartII = formLinesByPart['F1118_JPII'];
        this.groupLineCols(formLinesPartII, 'II', this.subTotalLinesPartII, null);

        const formLinesPartIII = formLinesByPart['F1118_JPIII'];
        this.groupLineCols(formLinesPartIII, 'III', this.subTotalLinesPartIII, this.recaptureLinesPartIII);

        const formLinesPartIV = formLinesByPart['F1118_JPIV'];
        this.groupLineCols(formLinesPartIV, 'IV', this.subTotalLinesPartIV, this.recaptureLinesPartIV);

        this.cdr.detectChanges();

        // Format editable inputs with commas after view renders (matching AngularJS $formatters)
        setTimeout(() => this.formatAllInputs(), 0);
      },
      (error: any) => {
        console.error('Error loading form lines:', error);
        this.showSpinner = false;
        this.cdr.detectChanges();
      }
    );
  }

  // ===================== DATA GROUPING =====================

  private groupLineCols(formLines: any[], partNo: string, subTotalLines: string[] | null, recaptureLines: string[] | null): void {
    if (!formLines) {
      this.showSpinner = false;
      return;
    }

    // Assign is_admin flag
    if (formLines[0] && formLines[0].IS_ADMIN === 'Y') {
      this.isAdmin = true;
    }

    const grouped = this.groupLineAndColumns(formLines, subTotalLines, recaptureLines);

    switch (partNo) {
      case 'I':
        this.groupedFormLines = grouped;
        break;
      case 'II':
        this.groupedFormLinesPartII = grouped;
        break;
      case 'III':
        this.groupedFormLinesPartIII = grouped;
        break;
      case 'IV':
        this.groupedFormLinesPartIV = grouped;
        break;
    }

    this.addTotals(partNo);
    this.showSpinner = false;
  }

  private groupLineAndColumns(formLines: any[], subTotalLines: string[] | null, recaptureLines: string[] | null): any[] {
    let init = 0;
    return _.reduce(
      formLines,
      (memo: any[], property: any) => {
        let bucket: any = _.find(memo, {
          SCHD_KEY: property.SCHD_KEY,
          LINE_KEY: property.LINE_KEY,
          LINE_NO: property.LINE_NO,
          LINE_DESCRIPTION: property.LINE_DESCRIPTION,
          LINE_ORDER: property.LINE_ORDER,
          BASKET_KEY: property.BASKET_KEY,
          BASKET_DESC: property.BASKET_DESC
        });

        if (!bucket) {
          bucket = {
            SCHD_KEY: property.SCHD_KEY,
            LINE_KEY: property.LINE_KEY,
            LINE_NO: property.LINE_NO,
            LINE_DESCRIPTION: property.LINE_DESCRIPTION,
            LINE_ORDER: property.LINE_ORDER,
            BASKET_KEY: property.BASKET_KEY,
            BASKET_DESC: property.BASKET_DESC,
            items: []
          };
          memo.push(bucket);
        }

        // Coerce AMOUNT to a real number to prevent string concatenation in addLines/subLines
        const rawAmount = property.AMOUNT;
        const numericAmount = (rawAmount != null && rawAmount !== '') ? Number(rawAmount) : null;

        const attribs: any = {
          SCHD_KEY: property.SCHD_KEY,
          LINE_KEY: property.LINE_KEY,
          BASKET_KEY: property.BASKET_KEY,
          COL_KEY: property.COL_KEY,
          COL_DESCRIPTION: property.COL_DESCRIPTION,
          COL_ORDER: property.COL_ORDER,
          IS_ENABLED: property.IS_DISPLAY,
          ATTRIB_AMOUNT: (numericAmount !== null && !isNaN(numericAmount)) ? numericAmount : null
        };

        if (init === 0 && attribs.BASKET_KEY) {
          init = 1;
          this.sourceKey = attribs.BASKET_KEY;
        }

        if (attribs.ATTRIB_AMOUNT != null) {
          attribs.OPERATION_TYPE = 'U';
        } else {
          attribs.OPERATION_TYPE = null;
        }

        if (this.isSkipFromSaving(bucket, attribs, subTotalLines, recaptureLines)) {
          attribs.OPERATION_TYPE = 'N'; // no crud
        }

        bucket.items.push(attribs);
        return memo;
      },
      []
    );
  }

  private isSkipFromSaving(bucket: any, attribs: any, subTotalLines: string[] | null, recaptureLines: string[] | null): boolean {
    if (attribs.IS_ENABLED === 'N'
        || (subTotalLines && _.findIndex(subTotalLines, (l: string) => l === bucket.LINE_NO?.replace('.', '')) !== -1)
        || (recaptureLines && _.findIndex(recaptureLines, (l: string) => l === bucket.LINE_NO?.replace('.', '')) !== -1)) {
      return true;
    }
    return false;
  }

  // ===================== TOTALS CALCULATION =====================

  addTotals(partNo: string): void {
    if (partNo === 'I' && this.groupedFormLines) {
      this.setRecaptureValuesAndAddLines();
      const revision = 2020;
      if (parseInt(this.selectedTaxYear.value) >= revision) {
        this.addLines(this.groupedFormLines, '3', '1', '2a', '2b', '2c', '2d', '2e1', '2e2');
      } else {
        this.addLines(this.groupedFormLines, '3', '1', '2a', '2b', '2c', '2d', '2e');
      }
      this.addLines(this.groupedFormLines, '6', '3', '4', '5');
      this.addLines(this.groupedFormLines, '8', '6', '7');
      if (parseInt(this.selectedTaxYear.value) >= revision) {
        this.addLines(this.groupedFormLines, '11', '8', '9a', '9b', '9c', '9d', '9e1', '9e2', '10');
      } else {
        this.addLines(this.groupedFormLines, '11', '8', '9a', '9b', '9c', '9d', '9e', '10');
      }
    } else if (partNo === 'II') {
      // No totals calculation for part II
    } else if (partNo === 'III') {
      this.setRecaptureValuesAndAddLines();
    } else if (partNo === 'IV') {
      this.setRecaptureValuesAndAddLines();
    }
  }

  private setRecaptureValuesAndAddLines(): void {
    if (this.groupedFormLines) {
      if (this.groupedFormLinesPartIII) {
        const partOneLine7 = _.findIndex(this.groupedFormLines, { LINE_NO: '7.' });
        const partThreeLine4 = _.findIndex(this.groupedFormLinesPartIII, { LINE_NO: '4.' });
        if (partOneLine7 >= 0 && partThreeLine4 >= 0) {
          for (let i = 0; i < this.groupedFormLinesPartIII[partThreeLine4].items.length; i++) {
            const val = this.groupedFormLines[partOneLine7].items[i].ATTRIB_AMOUNT;
            this.groupedFormLinesPartIII[partThreeLine4].items[i].ATTRIB_AMOUNT =
              val != null ? Number(val) || 0 : null;
          }
          this.addLines(this.groupedFormLinesPartIII, '5', '1', '2', '3', '4');
        }
      }
      if (this.groupedFormLinesPartIV) {
        const partOneLine10 = _.findIndex(this.groupedFormLines, { LINE_NO: '10.' });
        const partFourLine5 = _.findIndex(this.groupedFormLinesPartIV, { LINE_NO: '5.' });
        if (partOneLine10 >= 0 && partFourLine5 >= 0) {
          // LENGTH-1 to exclude last column (US Income)
          for (let i = 0; i < this.groupedFormLinesPartIV[partFourLine5].items.length - 1; i++) {
            const val = this.groupedFormLines[partOneLine10].items[i].ATTRIB_AMOUNT;
            this.groupedFormLinesPartIV[partFourLine5].items[i].ATTRIB_AMOUNT =
              val != null ? Number(val) || 0 : null;
          }
          this.addLines(this.groupedFormLinesPartIV, '4', '1', '2', '3');
          this.subLines(this.groupedFormLinesPartIV, '6', '4', '5');
        }
      }
    }
  }

  private addLines(groupedFormLinesObj: any[], totalsLineNo: string, ...inputLineNos: string[]): void {
    const totalsLineIndex = _.findIndex(groupedFormLinesObj, { LINE_NO: totalsLineNo + '.' });
    if (totalsLineIndex < 0) return;

    for (let inputLineNoIndex = 0; inputLineNoIndex < inputLineNos.length; inputLineNoIndex++) {
      const currentLineIndex = _.findIndex(groupedFormLinesObj, { LINE_NO: inputLineNos[inputLineNoIndex] + '.' });
      if (groupedFormLinesObj[currentLineIndex]) {
        for (let i = 0; i < groupedFormLinesObj[currentLineIndex].items.length; i++) {
          if (groupedFormLinesObj[totalsLineIndex].items[i]) {
            if (inputLineNoIndex === 0) {
              groupedFormLinesObj[totalsLineIndex].items[i].ATTRIB_AMOUNT = 0;
            }
            if (groupedFormLinesObj[totalsLineIndex].items[i].IS_ENABLED === 'N') {
              const currentVal = groupedFormLinesObj[currentLineIndex].items[i].ATTRIB_AMOUNT;
              groupedFormLinesObj[totalsLineIndex].items[i].ATTRIB_AMOUNT =
                Number(groupedFormLinesObj[totalsLineIndex].items[i].ATTRIB_AMOUNT || 0) +
                (currentVal != null ? Number(currentVal) || 0 : 0);
            } else {
              groupedFormLinesObj[totalsLineIndex].items[i].ATTRIB_AMOUNT = null;
            }
            if (inputLineNoIndex === (inputLineNos.length - 1)) {
              if (groupedFormLinesObj[totalsLineIndex].items[i].ATTRIB_AMOUNT === 0) {
                groupedFormLinesObj[totalsLineIndex].items[i].ATTRIB_AMOUNT = null;
              }
            }
          }
        }
      }
    }
  }

  private subLines(groupedFormLinesObj: any[], totalsLineNo: string, firstNo: string, secondNo: string): void {
    const totalsLineIndex = _.findIndex(groupedFormLinesObj, { LINE_NO: totalsLineNo + '.' });
    const firstNoIndex = _.findIndex(groupedFormLinesObj, { LINE_NO: firstNo + '.' });
    const secondNoIndex = _.findIndex(groupedFormLinesObj, { LINE_NO: secondNo + '.' });
    if (totalsLineIndex < 0 || firstNoIndex < 0 || secondNoIndex < 0) return;

    for (let i = 0; i < groupedFormLinesObj[totalsLineIndex].items.length; i++) {
      const firstAmount = groupedFormLinesObj[firstNoIndex].items[i].ATTRIB_AMOUNT;
      const secondAmount = groupedFormLinesObj[secondNoIndex].items[i].ATTRIB_AMOUNT;
      if (firstAmount == null && secondAmount == null) {
        groupedFormLinesObj[totalsLineIndex].items[i].ATTRIB_AMOUNT = null;
      } else {
        groupedFormLinesObj[totalsLineIndex].items[i].ATTRIB_AMOUNT =
          (firstAmount != null ? Number(firstAmount) || 0 : 0) -
          (secondAmount != null ? Number(secondAmount) || 0 : 0);
      }
    }
  }

  // ===================== RESET =====================

  reset(): void {
    this.resetSection(this.groupedFormLines);
    this.resetSection(this.groupedFormLinesPartII);
    this.resetSection(this.groupedFormLinesPartIII);
    this.resetSection(this.groupedFormLinesPartIV);
    this.showSpinner = true;
    this.cdr.detectChanges();
    this.getFormLinesAndDescriptions();
  }

  private resetSection(groupedFormLines: any[] | null): void {
    if (!groupedFormLines) return;
    for (const line of groupedFormLines) {
      for (const item of line.items) {
        item.ATTRIB_AMOUNT = null;
      }
    }
  }

  // ===================== SAVE (with gtw-confirmation-modal) =====================

  saveGeneralBasket(): void {
    this.modalConfig = {
      title: '1118 Schedule J',
      message: `Are you sure to save Schedule J data for Filer Entity: ${this.selectedFE?.FILER_LE || ''}`,
      iconClass: 'fa fa-sign-out',
      iconColor: 'txt-color-orange-dark',
      confirmButtonText: 'Yes',
      cancelButtonText: 'No',
      confirmButtonClass: 'btn-danger',
      cancelButtonClass: 'btn-default',
      showCancelButton: true,
      showConfirmButton: true,
      animationClass: 'animated fadeIn fast',
      loading: false,
      loadingText: 'Processing...'
    };
    this.showConfirmDialog = true;
    this.cdr.detectChanges();
  }

  onConfirmSave(): void {
    this.showConfirmDialog = false;
    this.cdr.detectChanges();
    this.performSave();
  }

  onCancelConfirm(): void {
    this.showConfirmDialog = false;
    this.cdr.detectChanges();
  }

  private performSave(): void {
    this.isSaveClicked = true;
    this.cdr.detectChanges();

    const dataToSave: any[] = [];

    const returnClobSettingsObj: any = {
      sso_id: this.logged_in_user,
      jcd_key: this.jcd_key,
      jcd_ta_key: this.jcd_ta_key
    };

    this.pushToDataToSave(this.groupedFormLines, dataToSave);
    this.pushToDataToSave(this.groupedFormLinesPartII, dataToSave);
    this.pushToDataToSave(this.groupedFormLinesPartIII, dataToSave);
    this.pushToDataToSave(this.groupedFormLinesPartIV, dataToSave);

    const message = 'Form 1118 Schedule-J Amount(s) have been saved/updated';
    this.sendDetails(returnClobSettingsObj, dataToSave, message);
  }

  private pushToDataToSave(groupedFormLinesObj: any[] | null, dataToSave: any[]): void {
    if (!groupedFormLinesObj) return;

    for (const line of groupedFormLinesObj) {
      for (const item of line.items) {
        const returnObj: any = {
          AMOUNT: item.ATTRIB_AMOUNT,
          REASON: null,
          SCHD_KEY: item.SCHD_KEY,
          LINE_KEY: item.LINE_KEY,
          COL_KEY: item.COL_KEY,
          BASKET_KEY: item.BASKET_KEY || this.sourceKey,
          OPERATION_TYPE: null
        };

        if (item.ATTRIB_AMOUNT != null && item.ATTRIB_AMOUNT !== 0 && item.OPERATION_TYPE === 'U') {
          returnObj.OPERATION_TYPE = item.OPERATION_TYPE;
        } else if (item.ATTRIB_AMOUNT != null && item.OPERATION_TYPE == null) {
          returnObj.OPERATION_TYPE = 'I';
          item.OPERATION_TYPE = 'U';
        } else if ((item.ATTRIB_AMOUNT == null || item.ATTRIB_AMOUNT === 0) && item.OPERATION_TYPE === 'U') {
          returnObj.OPERATION_TYPE = 'D';
          item.OPERATION_TYPE = null;
        } else if (item.OPERATION_TYPE === 'N') {
          returnObj.OPERATION_TYPE = null;
        }

        if (returnObj.OPERATION_TYPE != null) {
          dataToSave.push(returnObj);
        }
      }
    }
  }

  private sendDetails(returnClobSettingsObj: any, returnClobDataObj: any[], message: string): void {
    this.form1118SchdJService.saveBasketAmounts(
      this.GENERAL_CONFIG.base_url,
      returnClobSettingsObj,
      returnClobDataObj,
      this.selectedTaxYear.value,
      this.selectedScenario.VALUE,
      this.selectedFE.FILER_LE_KEY,
      this.selectedFE.FILER_COMBINATION_KEY,
      this.jcd_key,
      this.globalService,
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (result: any) => {
        // saveJSON returns response body directly; callSuccess is '1' (string) on success
        if (result?.callSuccess == '1') {
          this.gtwSnackbarService.openSnackBar(message, 'gtw-snackbar--green');
        } else {
          this.gtwSnackbarService.openSnackBar(
            result?.errorMessage || 'Error occurred while saving.',
            'gtw-snackbar--red'
          );
        }
        this.isSaveClicked = false;
        this.cdr.detectChanges();
      },
      (error: any) => {
        this.gtwSnackbarService.openSnackBar('Error occurred while saving. Please try again.', 'gtw-snackbar--red');
        this.isSaveClicked = false;
        this.cdr.detectChanges();
      }
    );
  }

  // ===================== HELPERS =====================

  trackByLineOrder(index: number, item: any): any {
    return item.LINE_ORDER || index;
  }

  trackByIndex(index: number): number {
    return index;
  }

  /**
   * Format number with commas for display (matching AngularJS $filter('number') behaviour).
   * e.g. 1234 → "1,234", -1234 → "-1,234"
   */
  formatNumber(value: any): string {
    if (value == null || value === '') return '';
    const num = Number(value);
    if (isNaN(num)) return '';
    return num.toLocaleString('en-US');
  }

  /**
   * After data loads and the view renders, format every visible editable text
   * input so the user sees comma-separated values on first paint —
   * replicating the AngularJS format="number" $formatters behaviour.
   */
  private formatAllInputs(): void {
    const inputs = this.el.nativeElement.querySelectorAll('input[type="text"]');
    inputs.forEach((input: HTMLInputElement) => {
      if (input.disabled) return; // disabled inputs use [value] binding
      const raw = (input.value || '').replace(/,/g, '');
      if (raw !== '' && raw !== '-') {
        const num = parseFloat(raw);
        if (!isNaN(num)) {
          input.value = num.toLocaleString('en-US');
        }
      }
    });
  }

  // ===================== FIELD FORMATTING (comma-separated numbers) =====================

  /**
   * On focus: strip commas so the user edits the raw number.
   */
  onFieldFocus(event: FocusEvent): void {
    const input = event.target as HTMLInputElement;
    input.value = input.value.replace(/,/g, '');
  }

  /**
   * On blur: re-format the value with commas.
   */
  onFieldBlur(event: FocusEvent, model: any, key: string): void {
    const input = event.target as HTMLInputElement;
    const raw = input.value;
    if (raw !== '' && raw !== '-') {
      const num = parseFloat(raw);
      if (!isNaN(num)) {
        input.value = num.toLocaleString('en-US');
      }
    }
  }

  /**
   * On keypress: only allow digits, decimal point, and minus sign.
   */
  onFieldKeypress(event: KeyboardEvent): void {
    const pattern = /[0-9.\-]/;
    const inputChar = String.fromCharCode(event.charCode);
    if (!pattern.test(inputChar)) {
      event.preventDefault();
    }
  }

  /**
   * On input: sanitize value – strip non-numeric characters, handle duplicates,
   * then write the parsed number back to the model so ngModel stays numeric.
   */
  onFieldInput(event: Event, model: any, key: string): void {
    const input = event.target as HTMLInputElement;
    let sanitized = input.value.replace(/[^0-9.\-]/g, '');

    // Keep only the first decimal point
    const dotIdx = sanitized.indexOf('.');
    if (dotIdx !== -1) {
      sanitized = sanitized.substring(0, dotIdx + 1) +
                  sanitized.substring(dotIdx + 1).replace(/\./g, '');
    }

    // Keep only a leading minus sign
    const hasLeadingMinus = sanitized.startsWith('-');
    sanitized = sanitized.replace(/-/g, '');
    if (hasLeadingMinus) {
      sanitized = '-' + sanitized;
    }

    input.value = sanitized;
    model[key] = (sanitized === '' || sanitized === '-' || sanitized === '.' || sanitized === '-.')
      ? null
      : parseFloat(sanitized);
  }

  ngOnDestroy(): void {
    this.gtwWorkspace.hideDataFilters.next(false);
    this.subs.unsubscribe();
  }
}
